import { useMemo } from "react";
import type {
  NotificationMetadataBase,
  NotificationRequester,
  ShellNotification,
} from "@shared/notifications/shell-notifications-contract";
import {
  resolveNotificationItem,
  type NotificationResolverContext,
} from "./notification-resolvers";
import type {
  NotificationCategory,
  NotificationDestination,
  NotificationFilters,
  NotificationItem,
  NotificationMetadata,
  NotificationTranslationParams,
  NotificationViewItem,
} from "./types";

interface UseResolvedNotificationsOptions {
  items: readonly ShellNotification<NotificationMetadataBase>[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  acknowledge: (id: string) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  isDeleting?: boolean;
  deletingId?: string;
  resolverContext: NotificationResolverContext;
}

export interface ResolvedNotificationsResult {
  items: NotificationViewItem[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  acknowledge: (id: string) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  isDeleting: boolean;
  deletingId?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const normalizeCategory = (
  value: unknown,
): NotificationCategory | undefined =>
  value === "action" || value === "info" ? value : undefined;

const normalizeNotificationMetadata = (
  metadata?: NotificationMetadataBase | null,
): NotificationMetadata | null | undefined => {
  if (metadata === null) {
    return null;
  }

  if (!metadata || !isRecord(metadata)) {
    return undefined;
  }

  const template = metadata.template;
  const params = Reflect.get(metadata, "params");
  const filters = Reflect.get(metadata, "filters");
  const destination = metadata.destination;

  return {
    ...metadata,
    category: normalizeCategory(metadata.category),
    destination: isRecord(destination)
      ? (destination as NotificationDestination)
      : undefined,
    filters: isRecord(filters) ? (filters as NotificationFilters) : undefined,
    params: isRecord(params)
      ? (params as NotificationTranslationParams)
      : undefined,
    template:
      typeof template === "string" && template.trim().length > 0
        ? template
        : undefined,
  };
};

const normalizeNotificationItem = (
  item: ShellNotification<NotificationMetadataBase>,
): NotificationItem => ({
  ...item,
  metadata: normalizeNotificationMetadata(item.metadata),
  requester: (item.requester as NotificationRequester | null | undefined) ?? null,
});

export const useResolvedNotifications = ({
  items,
  totalCount,
  isLoading,
  isFetching,
  error,
  refetch,
  acknowledge,
  deleteNotification,
  isDeleting = false,
  deletingId,
  resolverContext,
}: UseResolvedNotificationsOptions): ResolvedNotificationsResult => {
  const resolvedItems = useMemo(
    () =>
      items
        .map(normalizeNotificationItem)
        .map((item) => resolveNotificationItem(item, resolverContext))
        .filter((item): item is NotificationViewItem => Boolean(item))
        .sort((left, right) => right.view.priority - left.view.priority),
    [items, resolverContext],
  );

  return useMemo(
    () => ({
      items: resolvedItems,
      totalCount,
      isLoading,
      isFetching,
      error,
      refetch,
      acknowledge,
      deleteNotification,
      isDeleting,
      deletingId,
    }),
    [
      acknowledge,
      deleteNotification,
      deletingId,
      error,
      isDeleting,
      isFetching,
      isLoading,
      refetch,
      resolvedItems,
      totalCount,
    ],
  );
};
